import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, Zap, CloudRain, Shuffle, ArrowRight } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtMoney, fmtNum } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";

const SCENARIOS = [
  { key: "crash", label: "Crash Schedule", icon: Zap, blurb: "Increase welder / fitter manpower and measure the cost surge against timeline compression." },
  { key: "hindrance", label: "Hindrance Impact", icon: CloudRain, blurb: "Model a crane or front unavailability and compute delay damages plus extended overheads." },
  { key: "resource_shift", label: "Resource Trade-off", icon: Shuffle, blurb: "Shift manpower from float-rich activities onto the critical path at zero net cost." },
];

export default function WhatIf() {
  const { pid } = useParams();
  const [scenario, setScenario] = useState("crash");
  const [manpower, setManpower] = useState([30]);
  const [delay, setDelay] = useState([15]);
  const [onCritical, setOnCritical] = useState(true);
  const [moved, setMoved] = useState([20]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    const params =
      scenario === "crash" ? { manpower_increase_pct: manpower[0] }
      : scenario === "hindrance" ? { delay_days: delay[0], on_critical_path: onCritical }
      : { manpower_moved: moved[0] };
    try {
      const { data } = await api.post(`/projects/${pid}/whatif`, { scenario, params });
      setResult(data);
      toast.success("Scenario computed");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const active = SCENARIOS.find((s) => s.key === scenario);

  return (
    <div data-testid="whatif-page">
      <PageHead title="What-If Financial Scenario Modeler" subtitle="Simulate resource trade-offs, labour cost shifts and hindrance impacts before committing on site." />

      <div className="grid lg:grid-cols-3 gap-3">
        {SCENARIOS.map((s) => (
          <button
            key={s.key}
            onClick={() => { setScenario(s.key); setResult(null); }}
            className={`text-left border p-4 transition-colors duration-150 ${scenario === s.key ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"}`}
            data-testid={`scenario-${s.key}-btn`}
          >
            <s.icon className={`w-5 h-5 ${scenario === s.key ? "text-primary" : "text-muted-foreground"}`} />
            <h3 className="font-semibold mt-2">{s.label}</h3>
            <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">{s.blurb}</p>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mt-3">
        <Panel title={`Inputs — ${active.label}`} testId="whatif-inputs-panel">
          {scenario === "crash" ? (
            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider">Manpower increase</Label>
                  <span className="num text-primary font-semibold">{manpower[0]}%</span>
                </div>
                <Slider value={manpower} onValueChange={setManpower} min={5} max={120} step={5} data-testid="whatif-manpower-slider" />
              </div>
              <p className="text-[12px] text-muted-foreground">A diminishing-returns efficiency factor of 0.75 and a 15% overtime premium are applied automatically.</p>
            </div>
          ) : scenario === "hindrance" ? (
            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider">Unavailability duration</Label>
                  <span className="num text-primary font-semibold">{delay[0]} days</span>
                </div>
                <Slider value={delay} onValueChange={setDelay} min={1} max={90} step={1} data-testid="whatif-delay-slider" />
              </div>
              <div className="flex items-center justify-between border border-border p-3">
                <div>
                  <Label className="text-[13px]">Hits the critical path</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">If off the critical path, float absorbs part of the impact.</p>
                </div>
                <Switch checked={onCritical} onCheckedChange={setOnCritical} data-testid="whatif-critical-switch" />
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider">Manpower re-deployed to critical path</Label>
                  <span className="num text-primary font-semibold">{moved[0]}</span>
                </div>
                <Slider value={moved} onValueChange={setMoved} min={5} max={80} step={5} data-testid="whatif-moved-slider" />
              </div>
              <p className="text-[12px] text-muted-foreground">Non-critical activities slip within available float; total contract cost is unchanged.</p>
            </div>
          )}
          <Button className="w-full mt-5" onClick={run} disabled={busy} data-testid="whatif-run-btn">
            <FlaskConical className="w-4 h-4 mr-1.5" /> {busy ? "Simulating…" : "Run scenario"}
          </Button>
        </Panel>

        <Panel title="Simulation outcome" testId="whatif-result-panel">
          {!result ? (
            <p className="text-sm text-muted-foreground">Run a scenario to see the schedule and financial delta against the live baseline.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Kpi label="Baseline duration" value={`${result.baseline.duration_days} d`} />
                <Kpi label="Scenario duration" value={`${result.result.duration_days} d`} tone={result.result.duration_days <= result.baseline.duration_days ? "good" : "bad"} testId="whatif-duration-out" />
                <Kpi label="Baseline actual cost" value={fmtMoney(result.baseline.ac)} />
                <Kpi label="Scenario cost" value={fmtMoney(result.result.ac)} tone={result.result.cost_delta <= 0 ? "good" : "warn"} testId="whatif-cost-out" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge2 tone={result.result.days_saved > 0 ? "good" : "bad"}>
                  {result.result.days_saved > 0 ? `${result.result.days_saved} days saved` : `${result.result.days_lost || 0} days lost`}
                </Badge2>
                <Badge2 tone={result.result.cost_delta > 0 ? "warn" : "good"}>Cost delta {fmtMoney(result.result.cost_delta)}</Badge2>
                <Badge2 tone={result.result.new_cpi >= 1 ? "good" : "bad"}>Projected CPI {result.result.new_cpi}</Badge2>
                <Badge2 tone="primary">Baseline SPI {result.baseline.spi}</Badge2>
              </div>
              <div className="border border-border bg-muted/40 p-3">
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-[13px] leading-relaxed">{result.result.narrative}</p>
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Baseline EV {fmtMoney(result.baseline.ev)} · BAC {fmtMoney(result.baseline.bac)} · Physical progress {result.baseline.percent_complete}%
              </div>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
